import { z } from 'zod';

import { type AccountArrRange } from '@apps/web/routes/_app/admin/accounts/account_board_data';
import {
    accountChipOperator,
    type AccountFilterChip,
    type AccountFilterDimensionKey,
} from '@apps/web/routes/_app/admin/accounts/account_filters';

const optionalStringArraySearchParam = z
    .array(z.string())
    .optional()
    .catch(undefined);

export const accountFilterSearchSchema = z.object({
    health: optionalStringArraySearchParam,
    exclude_health: optionalStringArraySearchParam,
    stages: optionalStringArraySearchParam,
    exclude_stages: optionalStringArraySearchParam,
    kickoff_weeks: optionalStringArraySearchParam,
    exclude_kickoff_weeks: optionalStringArraySearchParam,
    owners: optionalStringArraySearchParam,
    exclude_owners: optionalStringArraySearchParam,
    tags: optionalStringArraySearchParam,
    exclude_tags: optionalStringArraySearchParam,
    features: optionalStringArraySearchParam,
    exclude_features: optionalStringArraySearchParam,
    view: z.string().optional().catch(undefined),
    // Contracted ARR bounds in whole dollars.
    arr_min: z.number().nonnegative().optional().catch(undefined),
    arr_max: z.number().nonnegative().optional().catch(undefined),
});

export type AccountFilterSearchParams = z.infer<
    typeof accountFilterSearchSchema
>;

type AccountFilterParams = {
    readonly health: Array<string> | undefined;
    readonly exclude_health: Array<string> | undefined;
    readonly stages: Array<string> | undefined;
    readonly exclude_stages: Array<string> | undefined;
    readonly kickoff_weeks: Array<string> | undefined;
    readonly exclude_kickoff_weeks: Array<string> | undefined;
    readonly owners: Array<string> | undefined;
    readonly exclude_owners: Array<string> | undefined;
    readonly tags: Array<string> | undefined;
    readonly exclude_tags: Array<string> | undefined;
    readonly features: Array<string> | undefined;
    readonly exclude_features: Array<string> | undefined;
};

/** Reads the URL's dollar ARR bounds as the cents range cards compare on. */
export function searchToAccountArrRange(
    search: AccountFilterSearchParams,
): AccountArrRange {
    return {
        minCents: search.arr_min === undefined ? null : search.arr_min * 100,
        maxCents: search.arr_max === undefined ? null : search.arr_max * 100,
    };
}

/** Returns whether the URL explicitly contains any account filter state. */
export function accountSearchHasFilterParams(
    search: AccountFilterSearchParams,
): boolean {
    return (
        search.health !== undefined ||
        search.exclude_health !== undefined ||
        search.stages !== undefined ||
        search.exclude_stages !== undefined ||
        search.kickoff_weeks !== undefined ||
        search.exclude_kickoff_weeks !== undefined ||
        search.owners !== undefined ||
        search.exclude_owners !== undefined ||
        search.tags !== undefined ||
        search.exclude_tags !== undefined ||
        search.features !== undefined ||
        search.exclude_features !== undefined
    );
}

/** Maps account filter URL params to chips in stable display order. */
export function searchToAccountFilterChips(
    search: AccountFilterSearchParams,
): Array<AccountFilterChip> {
    const chips: Array<AccountFilterChip> = [];
    const pushChip = (
        dimension: AccountFilterDimensionKey,
        operator: NonNullable<AccountFilterChip['operator']>,
        values: Array<string> | undefined,
    ) => {
        if (values && values.length > 0) {
            chips.push({
                dimension,
                operator,
                values: [...new Set(values)],
            });
        }
    };

    pushChip('health', 'is', search.health);
    pushChip('health', 'is_not', search.exclude_health);
    pushChip('stage', 'is', search.stages);
    pushChip('stage', 'is_not', search.exclude_stages);
    pushChip('kickoff_week', 'is', search.kickoff_weeks);
    pushChip('kickoff_week', 'is_not', search.exclude_kickoff_weeks);
    pushChip('owner', 'is', search.owners);
    pushChip('owner', 'is_not', search.exclude_owners);
    pushChip('tag', 'is', search.tags);
    pushChip('tag', 'is_not', search.exclude_tags);
    pushChip('feature', 'is', search.features);
    pushChip('feature', 'is_not', search.exclude_features);

    return chips;
}

/**
 * Maps chips to every account filter URL param so absent filters clear stale
 * URL state. Chips sharing a dimension and operator are merged in input order.
 */
export function accountFilterChipsToSearchParams(
    chips: Array<AccountFilterChip>,
): AccountFilterParams {
    const valuesByDimension = {
        health: { is: new Set<string>(), is_not: new Set<string>() },
        stage: { is: new Set<string>(), is_not: new Set<string>() },
        kickoff_week: { is: new Set<string>(), is_not: new Set<string>() },
        owner: { is: new Set<string>(), is_not: new Set<string>() },
        tag: { is: new Set<string>(), is_not: new Set<string>() },
        feature: { is: new Set<string>(), is_not: new Set<string>() },
    };

    for (const chip of chips) {
        const operator = accountChipOperator(chip.operator);
        const values = valuesByDimension[chip.dimension][operator];
        for (const value of chip.values) {
            values.add(value);
        }
    }

    const valuesOrUndefined = (values: Set<string>) =>
        values.size > 0 ? [...values] : undefined;

    return {
        health: valuesOrUndefined(valuesByDimension.health.is),
        exclude_health: valuesOrUndefined(valuesByDimension.health.is_not),
        stages: valuesOrUndefined(valuesByDimension.stage.is),
        exclude_stages: valuesOrUndefined(valuesByDimension.stage.is_not),
        kickoff_weeks: valuesOrUndefined(valuesByDimension.kickoff_week.is),
        exclude_kickoff_weeks: valuesOrUndefined(
            valuesByDimension.kickoff_week.is_not,
        ),
        owners: valuesOrUndefined(valuesByDimension.owner.is),
        exclude_owners: valuesOrUndefined(valuesByDimension.owner.is_not),
        tags: valuesOrUndefined(valuesByDimension.tag.is),
        exclude_tags: valuesOrUndefined(valuesByDimension.tag.is_not),
        features: valuesOrUndefined(valuesByDimension.feature.is),
        exclude_features: valuesOrUndefined(valuesByDimension.feature.is_not),
    };
}
